import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import apiService from "../services/api";
import toast from "react-hot-toast";

// Helper to build tree from flat list
function buildTree(categories) {
  const map = {};
  const roots = [];
  categories.forEach((cat) => (map[cat.id] = { ...cat, children: [] }));
  categories.forEach((cat) => {
    if (cat.parentId) {
      map[cat.parentId]?.children.push(map[cat.id]);
    } else {
      roots.push(map[cat.id]);
    }
  });
  return roots;
}

// Hierarchical Category Selector Component
const CategorySelector = ({
  categories,
  selectedCategoryId,
  onCategoryChange,
  loading,
  label = "Category",
}) => {
  const [expanded, setExpanded] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);

  const tree = buildTree(categories);

  const handleToggle = (id) => {
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const getCategoryPath = (categoryId) => {
    const category = categories.find((cat) => cat.id === categoryId);
    if (!category) return "";

    const path = [category.name];
    let current = category;

    while (current.parentId) {
      current = categories.find((cat) => cat.id === current.parentId);
      if (current) {
        path.unshift(current.name);
      } else {
        break;
      }
    }

    return path.join(" > ");
  };

  const renderCategoryOption = (node, level = 0) => {
    const hasChildren = node.children && node.children.length > 0;
    const isExpanded = expanded[node.id];
    const isSelected = selectedCategoryId === node.id;

    return (
      <div key={node.id}>
        <div
          className={`flex items-center px-3 py-2 cursor-pointer hover:bg-blue-50 transition-colors duration-200 ${
            isSelected ? "bg-blue-100 text-blue-700" : ""
          }`}
          style={{ paddingLeft: `${level * 20 + 12}px` }}
          onClick={() => {
            onCategoryChange(node.id);
            setShowDropdown(false);
          }}
        >
          {hasChildren && (
            <button
              className="w-4 h-4 mr-2 flex items-center justify-center text-gray-500 hover:text-gray-700"
              onClick={(e) => {
                e.stopPropagation();
                handleToggle(node.id);
              }}
            >
              <svg
                className={`w-3 h-3 transition-transform duration-200 ${
                  isExpanded ? "rotate-90" : ""
                }`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </button>
          )}
          {!hasChildren && <div className="w-4 h-4 mr-2"></div>}
          <div className="flex items-center gap-2">
            <span className="text-sm">{node.name}</span>
            <span className="text-xs bg-blue-100 text-blue-600 px-1.5 py-0.5 rounded font-mono">
              {node.code}
            </span>
          </div>
        </div>
        {hasChildren && isExpanded && (
          <div>
            {node.children.map((child) =>
              renderCategoryOption(child, level + 1)
            )}
          </div>
        )}
      </div>
    );
  };

  const filteredTree = searchTerm
    ? tree.filter(
        (node) =>
          node.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          node.children.some((child) =>
            child.name.toLowerCase().includes(searchTerm.toLowerCase())
          )
      )
    : tree;

  return (
    <div className="relative">
      <label className="block text-sm font-semibold text-gray-700 mb-2">
        {label}
      </label>
      <div className="relative">
        <button
          type="button"
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-left focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
          onClick={() => setShowDropdown(!showDropdown)}
          disabled={loading}
        >
          {loading ? (
            <span className="text-gray-500">Loading categories...</span>
          ) : selectedCategoryId ? (
            <div className="flex items-center gap-2">
              <span className="text-gray-900">
                {getCategoryPath(selectedCategoryId)}
              </span>
              {(() => {
                const category = categories.find(
                  (cat) => cat.id === selectedCategoryId
                );
                return category ? (
                  <span className="text-xs bg-blue-100 text-blue-600 px-1.5 py-0.5 rounded font-mono">
                    {category.code}
                  </span>
                ) : null;
              })()}
            </div>
          ) : (
            <span className="text-gray-500">Select Category</span>
          )}
          <svg
            className={`absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 transition-transform duration-200 ${
              showDropdown ? "rotate-180" : ""
            }`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </button>

        {showDropdown && (
          <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
            <div className="p-2 border-b border-gray-200">
              <input
                type="text"
                placeholder="Search categories..."
                className="w-full px-3 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                autoFocus
              />
            </div>
            <div className="py-1">
              {filteredTree.length === 0 ? (
                <div className="px-3 py-2 text-sm text-gray-500">
                  No categories found
                </div>
              ) : (
                filteredTree.map((node) => renderCategoryOption(node))
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const EditProduct = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({
    // Basic Information (Product Level)
    name: "",
    itemCode: "",
    description: "",
    detailedDescription: "",
    category: "",
    subCategory: "",
    primaryCategoryId: null, // New field for multilevel categorization
    brand: "",
    model: "",

    // Pricing (Product Level)
    gstPercent: "",
    discountEligibility: false,

    // SKUs Array
    skus: [],

    // Specifications
    itemType: "",
    productLine: "",
    colorCode: "",
    baseType: "",
    finishType: "",
    unitOfMeasure: "",
    packSize: "",
    material: "",
    grade: "",

    // Industrial Features
    hsCode: "",
    isHazmat: false,
    countryOfOrigin: "",
    batchControlled: false,
    shelfLifeMonths: "",
    storageConditions: "",
    specialInstructions: "",

    // Inventory Management
    minimumOrderQuantity: "1",
    maximumOrderQuantity: "",
    leadTime: "",
    reorderPoint: "",
    isActive: true,

    // Variants
    variants: [],

    // Packaging Levels
    packagingLevels: [],

    // Images
    imageUrls: [],

    // Advanced Features
    specifications: {},
    industryFeatures: {},
    packagingOptions: {},
    visibilityRoles: [],
  });

  const [activeTab, setActiveTab] = useState("basic");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [uploadingImages, setUploadingImages] = useState(false);
  const [categories, setCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(false);

  // Predefined options
  const productTypes = [
    "Raw Material",
    "Finished Good",
    "Semi-Finished",
    "Component",
    "Assembly",
    "Tool",
    "Equipment",
    "Consumable",
  ];

  const unitsOfMeasure = [
    "Pieces",
    "Kilograms",
    "Meters",
    "Liters",
    "Square Meters",
    "Cubic Meters",
    "Boxes",
    "Cartons",
    "Pallets",
  ];

  const materials = [
    "Steel",
    "Aluminum",
    "Copper",
    "Plastic",
    "Wood",
    "Glass",
    "Ceramic",
    "Composite",
    "Other",
  ];

  const grades = [
    "Commercial",
    "Industrial",
    "Premium",
    "Standard",
    "High Grade",
    "Low Grade",
    "Food Grade",
    "Medical Grade",
  ];

  // Fetch categories from API
  const fetchCategories = async () => {
    try {
      setLoadingCategories(true);
      const data = await apiService.getCategories();
      setCategories(data);
    } catch (error) {
      console.error("Error fetching categories:", error);
    } finally {
      setLoadingCategories(false);
    }
  };

  // Handle category selection
  const handleCategoryChange = (categoryId) => {
    const category = categories.find((cat) => cat.id === parseInt(categoryId));
    setForm((prev) => ({
      ...prev,
      primaryCategoryId: categoryId ? parseInt(categoryId) : null,
      category: category ? category.name : "",
      subCategory: "", // Reset subcategory when category changes
    }));
  };

  // Fetch product data
  const fetchProduct = async () => {
    try {
      setLoading(true);
      const product = await apiService.getProduct(id);

      setForm({
        name: product.name || "",
        itemCode: product.itemCode || "",
        description: product.description || "",
        detailedDescription: product.detailedDescription || "",
        category: product.category || "",
        subCategory: product.subCategory || "",
        primaryCategoryId: product.primaryCategoryId || null,
        brand: product.brand || "",
        model: product.model || "",
        costPrice: product.costPrice || "",
        sellingPrice: product.sellingPrice || "",
        gstPercent: product.gstPercent || "",
        discountEligibility: product.discountEligibility || false,
        skus: product.skus || [],
        itemType: product.itemType || "",
        productLine: product.productLine || "",
        colorCode: product.colorCode || "",
        baseType: product.baseType || "",
        finishType: product.finishType || "",
        unitOfMeasure: product.unitOfMeasure || "",
        packSize: product.packSize || "",
        weight: product.weight || "",
        dimensions: product.dimensions || "",
        material: product.material || "",
        grade: product.grade || "",
        hsCode: product.hsCode || "",
        isHazmat: product.isHazmat || false,
        countryOfOrigin: product.countryOfOrigin || "",
        batchControlled: product.batchControlled || false,
        shelfLifeMonths: product.shelfLifeMonths || "",
        storageConditions: product.storageConditions || "",
        specialInstructions: product.specialInstructions || "",
        minimumOrderQuantity: product.minimumOrderQuantity || "1",
        maximumOrderQuantity: product.maximumOrderQuantity || "",
        leadTime: product.leadTime || "",
        reorderPoint: product.reorderPoint || "",
        isActive: product.isActive !== undefined ? product.isActive : true,
        variants: product.variants || [],
        packagingLevels: product.packagingLevels || [],
        imageUrls: product.imageUrls || [],
        specifications: product.specifications || {},
        industryFeatures: product.industryFeatures || {},
        packagingOptions: product.packagingOptions || {},
        visibilityRoles: product.visibilityRoles || [],
      });
    } catch (error) {
      toast.error("Failed to load product");
      navigate("/admin/products");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
    fetchProduct();
  }, [id]);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const addVariant = () => {
    setForm((prev) => ({
      ...prev,
      variants: [
        ...prev.variants,
        {
          id: Date.now(),
          name: "",
          sku: "",
          price: "",
          bulkPricePerLiter: "",
          weight: "",
          isActive: true,
        },
      ],
    }));
  };

  const removeVariant = (index) => {
    setForm((prev) => ({
      ...prev,
      variants: prev.variants.filter((_, i) => i !== index),
    }));
  };

  const updateVariant = (index, field, value) => {
    setForm((prev) => ({
      ...prev,
      variants: prev.variants.map((variant, i) =>
        i === index ? { ...variant, [field]: value } : variant
      ),
    }));
  };

  // SKU Management Functions
  const addSku = () => {
    setForm((prev) => ({
      ...prev,
      skus: [
        ...prev.skus,
        {
          id: Date.now(),
          sku: "",
          barcode: "",
          name: "",
          description: "",
          costPrice: "",
          sellingPrice: "",
          weight: "",
          dimensions: "",
          isActive: true,
        },
      ],
    }));
  };

  const removeSku = (index) => {
    setForm((prev) => ({
      ...prev,
      skus: prev.skus.filter((_, i) => i !== index),
    }));
  };

  const updateSku = (index, field, value) => {
    setForm((prev) => ({
      ...prev,
      skus: prev.skus.map((sku, i) =>
        i === index ? { ...sku, [field]: value } : sku
      ),
    }));
  };

  // Image upload handlers
  const handleImageUpload = async (event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;

    setUploadingImages(true);
    try {
      const imagePromises = files.map((file) => {
        return new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });
      });

      const base64Images = await Promise.all(imagePromises);
      setForm((prev) => ({
        ...prev,
        imageUrls: [...prev.imageUrls, ...base64Images],
      }));
      toast.success(`${files.length} image(s) uploaded successfully`);
    } catch (error) {
      console.error("Error uploading images:", error);
      toast.error("Failed to upload images");
    } finally {
      setUploadingImages(false);
    }
  };

  const handleRemoveImage = (index) => {
    setForm((prev) => ({
      ...prev,
      imageUrls: prev.imageUrls.filter((_, i) => i !== index),
    }));
    toast.success("Image removed");
  };

  const handleSetPrimaryImage = (index) => {
    if (index === 0) return; // Already primary
    setForm((prev) => {
      const newImageUrls = [...prev.imageUrls];
      const [primaryImage] = newImageUrls.splice(index, 1);
      newImageUrls.unshift(primaryImage);
      return { ...prev, imageUrls: newImageUrls };
    });
    toast.success("Primary image updated");
  };

  const addPackagingLevel = () => {
    setForm((prev) => ({
      ...prev,
      packagingLevels: [
        ...prev.packagingLevels,
        {
          id: Date.now(),
          level: "",
          description: "",
          quantity: "",
          weight: "",
          dimensions: "",
          material: "",
          cost: "",
        },
      ],
    }));
  };

  const removePackagingLevel = (index) => {
    setForm((prev) => ({
      ...prev,
      packagingLevels: prev.packagingLevels.filter((_, i) => i !== index),
    }));
  };

  const updatePackagingLevel = (index, field, value) => {
    setForm((prev) => ({
      ...prev,
      packagingLevels: prev.packagingLevels.map((level, i) =>
        i === index ? { ...level, [field]: value } : level
      ),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);

    try {
      // Validation
      if (!form.name || !form.itemCode) {
        throw new Error("Name and Item Code are required");
      }

      if (form.costPrice && parseFloat(form.costPrice) < 0) {
        throw new Error("Cost Price must be a positive number");
      }

      if (form.sellingPrice && parseFloat(form.sellingPrice) < 0) {
        throw new Error("Selling Price must be a positive number");
      }

      // Prepare data
      const saveData = {
        ...form,
        costPrice: form.costPrice ? parseFloat(form.costPrice) : null,
        sellingPrice: form.sellingPrice ? parseFloat(form.sellingPrice) : null,
        gstPercent: form.gstPercent ? parseFloat(form.gstPercent) : null,
        weight: form.weight ? parseFloat(form.weight) : null,
        shelfLifeMonths: form.shelfLifeMonths
          ? parseInt(form.shelfLifeMonths)
          : null,
        minimumOrderQuantity: form.minimumOrderQuantity
          ? parseInt(form.minimumOrderQuantity)
          : 1,
        maximumOrderQuantity: form.maximumOrderQuantity
          ? parseInt(form.maximumOrderQuantity)
          : null,
        leadTime: form.leadTime ? parseInt(form.leadTime) : null,
        reorderPoint: form.reorderPoint ? parseInt(form.reorderPoint) : null,
      };

      // API Call
      await apiService.updateProduct(id, saveData);
      toast.success("Product updated successfully");
      navigate("/admin/products");
    } catch (err) {
      setError(err.message);
      toast.error(err.message || "Failed to update product");
    } finally {
      setSubmitting(false);
    }
  };

  const tabs = [
    { id: "basic", label: "Basic Info", icon: "📦" },
    { id: "media", label: "Media", icon: "🖼️" },
    { id: "skus", label: "SKUs & Barcodes", icon: "🏷️" },
    { id: "specifications", label: "Specifications", icon: "⚙️" },
    { id: "variants", label: "Variants", icon: "🔄" },
    { id: "packaging", label: "Packaging", icon: "📦" },
    { id: "industrial", label: "Industrial", icon: "🏭" },
    { id: "advanced", label: "Advanced", icon: "🚀" },
  ];

  if (loading) {
    return (
      <div className="w-full min-h-screen bg-gray-50 py-6 px-4 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <p className="mt-4 text-gray-600">Loading product...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full min-h-screen bg-gray-50 py-6 px-4">
      <div className="w-full">
        <div className="mb-6">
          <button
            onClick={() => navigate("/admin/products")}
            className="flex items-center text-gray-600 hover:text-gray-900 transition-colors mb-4"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back to Products
          </button>
          <h1 className="text-3xl font-bold text-gray-900">Edit Product</h1>
          <p className="mt-2 text-gray-600">
            Update product details and specifications
          </p>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          {/* Tab Navigation */}
          <div className="bg-gray-50 border-b border-gray-200">
            <div className="flex space-x-1 p-4 overflow-x-auto">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-medium transition-all whitespace-nowrap ${
                    activeTab === tab.id
                      ? "bg-white text-blue-600 shadow-sm border border-blue-200"
                      : "text-gray-600 hover:text-gray-900 hover:bg-white/50"
                  }`}
                >
                  <span>{tab.icon}</span>
                  <span>{tab.label}</span>
                </button>
              ))}
            </div>
          </div>

          <form
            onSubmit={handleSubmit}
            className="p-6 max-h-[70vh] overflow-y-auto"
          >
            {/* Basic Information Tab */}
            {activeTab === "basic" && (
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Product Name *
                    </label>
                    <input
                      type="text"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      value={form.name}
                      onChange={(e) => handleChange("name", e.target.value)}
                      placeholder="e.g., High-Grade Steel Pipe"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Item Code *
                    </label>
                    <input
                      type="text"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      value={form.itemCode}
                      onChange={(e) => handleChange("itemCode", e.target.value)}
                      placeholder="e.g., SP-2IN-001"
                      required
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Brand
                    </label>
                    <input
                      type="text"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      value={form.brand}
                      onChange={(e) => handleChange("brand", e.target.value)}
                      placeholder="e.g., IndustrialCorp"
                    />
                  </div>
                </div>

                <div>
                  <CategorySelector
                    categories={categories}
                    selectedCategoryId={form.primaryCategoryId}
                    onCategoryChange={handleCategoryChange}
                    loading={loadingCategories}
                    label="Product Category"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Cost Price
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      value={form.costPrice}
                      onChange={(e) =>
                        handleChange("costPrice", e.target.value)
                      }
                      placeholder="0.00"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Selling Price
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      value={form.sellingPrice}
                      onChange={(e) =>
                        handleChange("sellingPrice", e.target.value)
                      }
                      placeholder="0.00"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      GST %
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      value={form.gstPercent}
                      onChange={(e) =>
                        handleChange("gstPercent", e.target.value)
                      }
                      placeholder="18.00"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Description
                  </label>
                  <textarea
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    value={form.description}
                    onChange={(e) =>
                      handleChange("description", e.target.value)
                    }
                    placeholder="Brief product description"
                    rows={3}
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Detailed Description
                  </label>
                  <textarea
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    value={form.detailedDescription}
                    onChange={(e) =>
                      handleChange("detailedDescription", e.target.value)
                    }
                    placeholder="Comprehensive product details, specifications, applications..."
                    rows={4}
                  />
                </div>
              </div>
            )}

            {/* Media Tab */}
            {activeTab === "media" && (
              <div className="space-y-6">
                <div className="flex justify-between items-center mb-6">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      Product Images
                    </h3>
                    <p className="text-sm text-gray-500 mt-1">
                      Upload multiple product images. First image will be the
                      primary display image.
                    </p>
                  </div>
                </div>

                {/* Image Upload Area */}
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-blue-400 transition-colors">
                  <input
                    type="file"
                    id="image-upload"
                    multiple
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="hidden"
                    disabled={uploadingImages}
                  />
                  <label
                    htmlFor="image-upload"
                    className="cursor-pointer flex flex-col items-center"
                  >
                    <svg
                      className="w-12 h-12 text-gray-400 mb-3"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                      />
                    </svg>
                    <span className="text-sm font-medium text-gray-700">
                      {uploadingImages
                        ? "Uploading..."
                        : "Click to upload images"}
                    </span>
                    <span className="text-xs text-gray-500 mt-1">
                      PNG, JPG, GIF up to 10MB each
                    </span>
                  </label>
                </div>

                {/* Image Gallery */}
                {form.imageUrls && form.imageUrls.length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold text-gray-700 mb-3">
                      Uploaded Images ({form.imageUrls.length})
                    </h4>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                      {form.imageUrls.map((url, index) => (
                        <div
                          key={index}
                          className="relative group border rounded-lg overflow-hidden bg-gray-50"
                        >
                          {index === 0 && (
                            <div className="absolute top-2 left-2 z-10">
                              <span className="bg-blue-500 text-white text-xs px-2 py-1 rounded-full">
                                Primary
                              </span>
                            </div>
                          )}
                          <img
                            src={url}
                            alt={`Product ${index + 1}`}
                            className="w-full h-48 object-cover"
                          />
                          <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 transition-all duration-200 flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100">
                            {index !== 0 && (
                              <button
                                type="button"
                                onClick={() => handleSetPrimaryImage(index)}
                                className="bg-white text-gray-700 px-3 py-1 rounded-lg text-sm font-medium hover:bg-gray-100 transition-colors"
                              >
                                Set Primary
                              </button>
                            )}
                            <button
                              type="button"
                              onClick={() => handleRemoveImage(index)}
                              className="bg-red-500 text-white px-3 py-1 rounded-lg text-sm font-medium hover:bg-red-600 transition-colors"
                            >
                              Remove
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {form.imageUrls.length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    <p>No images uploaded yet</p>
                  </div>
                )}
              </div>
            )}

            {/* SKUs & Barcodes Tab */}
            {activeTab === "skus" && (
              <div className="space-y-6">
                <div className="flex justify-between items-center">
                  <h3 className="text-lg font-semibold text-gray-900">
                    Product SKUs & Barcodes
                  </h3>
                  <button
                    type="button"
                    onClick={addSku}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    + Add SKU
                  </button>
                </div>

                {form.skus.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <p>
                      No SKUs added yet. Click "Add SKU" to create the first SKU
                      for this product.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {form.skus.map((sku, index) => (
                      <div
                        key={sku.id}
                        className="border border-gray-200 rounded-lg p-4 bg-gray-50"
                      >
                        <div className="flex justify-between items-center mb-4">
                          <h4 className="font-semibold text-gray-900">
                            SKU #{index + 1}
                          </h4>
                          <button
                            type="button"
                            onClick={() => removeSku(index)}
                            className="text-red-600 hover:text-red-800"
                          >
                            Remove
                          </button>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              SKU Code *
                            </label>
                            <input
                              type="text"
                              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                              value={sku.sku}
                              onChange={(e) =>
                                updateSku(index, "sku", e.target.value)
                              }
                              placeholder="e.g., SKU-001"
                              required
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Barcode *
                            </label>
                            <input
                              type="text"
                              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                              value={sku.barcode}
                              onChange={(e) =>
                                updateSku(index, "barcode", e.target.value)
                              }
                              placeholder="e.g., 8901234567890"
                              required
                            />
                          </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              SKU Name
                            </label>
                            <input
                              type="text"
                              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                              value={sku.name}
                              onChange={(e) =>
                                updateSku(index, "name", e.target.value)
                              }
                              placeholder="e.g., Black Size 42"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Cost Price
                            </label>
                            <input
                              type="number"
                              step="0.01"
                              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                              value={sku.costPrice}
                              onChange={(e) =>
                                updateSku(index, "costPrice", e.target.value)
                              }
                              placeholder="0.00"
                            />
                          </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Selling Price
                            </label>
                            <input
                              type="number"
                              step="0.01"
                              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                              value={sku.sellingPrice}
                              onChange={(e) =>
                                updateSku(index, "sellingPrice", e.target.value)
                              }
                              placeholder="0.00"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Weight (kg)
                            </label>
                            <input
                              type="number"
                              step="0.01"
                              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                              value={sku.weight}
                              onChange={(e) =>
                                updateSku(index, "weight", e.target.value)
                              }
                              placeholder="0.00"
                            />
                          </div>
                        </div>

                        <div className="mt-4">
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Dimensions
                          </label>
                          <input
                            type="text"
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            value={sku.dimensions}
                            onChange={(e) =>
                              updateSku(index, "dimensions", e.target.value)
                            }
                            placeholder="e.g., 100cm x 50cm x 25cm"
                          />
                        </div>

                        <div className="mt-4">
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Description
                          </label>
                          <textarea
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            value={sku.description}
                            onChange={(e) =>
                              updateSku(index, "description", e.target.value)
                            }
                            placeholder="SKU-specific description"
                            rows={2}
                          />
                        </div>

                        <div className="mt-4">
                          <label className="flex items-center">
                            <input
                              type="checkbox"
                              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                              checked={sku.isActive}
                              onChange={(e) =>
                                updateSku(index, "isActive", e.target.checked)
                              }
                            />
                            <span className="ml-2 text-sm text-gray-700">
                              Active
                            </span>
                          </label>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Specifications Tab */}
            {activeTab === "specifications" && (
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Product Type
                    </label>
                    <select
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      value={form.itemType}
                      onChange={(e) => handleChange("itemType", e.target.value)}
                    >
                      <option value="">Select Type</option>
                      {productTypes.map((type) => (
                        <option key={type} value={type}>
                          {type}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Unit of Measure
                    </label>
                    <select
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      value={form.unitOfMeasure}
                      onChange={(e) =>
                        handleChange("unitOfMeasure", e.target.value)
                      }
                    >
                      <option value="">Select Unit</option>
                      {unitsOfMeasure.map((unit) => (
                        <option key={unit} value={unit}>
                          {unit}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Material
                    </label>
                    <select
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      value={form.material}
                      onChange={(e) => handleChange("material", e.target.value)}
                    >
                      <option value="">Select Material</option>
                      {materials.map((material) => (
                        <option key={material} value={material}>
                          {material}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Weight (kg)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      value={form.weight}
                      onChange={(e) => handleChange("weight", e.target.value)}
                      placeholder="0.00"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Dimensions
                    </label>
                    <input
                      type="text"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      value={form.dimensions}
                      onChange={(e) =>
                        handleChange("dimensions", e.target.value)
                      }
                      placeholder="e.g., 100cm x 50cm x 25cm"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Grade
                    </label>
                    <select
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      value={form.grade}
                      onChange={(e) => handleChange("grade", e.target.value)}
                    >
                      <option value="">Select Grade</option>
                      {grades.map((grade) => (
                        <option key={grade} value={grade}>
                          {grade}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Brand
                    </label>
                    <input
                      type="text"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      value={form.brand}
                      onChange={(e) => handleChange("brand", e.target.value)}
                      placeholder="e.g., Tata Steel"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Model
                    </label>
                    <input
                      type="text"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      value={form.model}
                      onChange={(e) => handleChange("model", e.target.value)}
                      placeholder="e.g., TS-2024"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Variants Tab */}
            {activeTab === "variants" && (
              <div className="space-y-6">
                <div className="flex justify-between items-center">
                  <h3 className="text-lg font-semibold text-gray-900">
                    Pack Size Variants & Pricing
                  </h3>
                  <button
                    type="button"
                    onClick={addVariant}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                  >
                    + Add Pack Size
                  </button>
                </div>

                {form.variants.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <span className="text-4xl mb-4 block">📦</span>
                    <p>
                      No pack size variants added yet. Click "Add Pack Size" to
                      create pricing variants.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {form.variants.map((variant, index) => (
                      <div
                        key={variant.id}
                        className="border border-gray-200 rounded-lg p-4 bg-gray-50"
                      >
                        <div className="flex justify-between items-center mb-4">
                          <h4 className="font-medium text-gray-900">
                            Pack Size {index + 1}
                          </h4>
                          <button
                            type="button"
                            onClick={() => removeVariant(index)}
                            className="text-red-600 hover:text-red-800"
                          >
                            Remove
                          </button>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Pack Size
                            </label>
                            <input
                              type="text"
                              placeholder="e.g., 1.5 LT, 7.5 KG, 4 LT"
                              value={variant.name}
                              onChange={(e) =>
                                updateVariant(index, "name", e.target.value)
                              }
                              className="w-full border border-gray-300 rounded px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Price (₹)
                            </label>
                            <input
                              type="number"
                              step="0.01"
                              placeholder="0.00"
                              value={variant.price}
                              onChange={(e) =>
                                updateVariant(index, "price", e.target.value)
                              }
                              className="w-full border border-gray-300 rounded px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Bulk P/Ltr (₹)
                            </label>
                            <input
                              type="number"
                              step="0.01"
                              placeholder="0.00"
                              value={variant.bulkPricePerLiter}
                              onChange={(e) =>
                                updateVariant(
                                  index,
                                  "bulkPricePerLiter",
                                  e.target.value
                                )
                              }
                              className="w-full border border-gray-300 rounded px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                          </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              SKU
                            </label>
                            <input
                              type="text"
                              placeholder="Product SKU for this pack size"
                              value={variant.sku}
                              onChange={(e) =>
                                updateVariant(index, "sku", e.target.value)
                              }
                              className="w-full border border-gray-300 rounded px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Weight (kg)
                            </label>
                            <input
                              type="number"
                              step="0.01"
                              placeholder="0.00"
                              value={variant.weight}
                              onChange={(e) =>
                                updateVariant(index, "weight", e.target.value)
                              }
                              className="w-full border border-gray-300 rounded px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Packaging Tab */}
            {activeTab === "packaging" && (
              <div className="space-y-6">
                <div className="flex justify-between items-center">
                  <h3 className="text-lg font-semibold text-gray-900">
                    Packaging Levels
                  </h3>
                  <button
                    type="button"
                    onClick={addPackagingLevel}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                  >
                    Add Packaging Level
                  </button>
                </div>

                {form.packagingLevels.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <span className="text-4xl mb-4 block">📦</span>
                    <p>
                      No packaging levels defined. Click "Add Packaging Level"
                      to define packaging hierarchy.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {form.packagingLevels.map((level, index) => (
                      <div
                        key={level.id}
                        className="border border-gray-200 rounded-lg p-4 bg-gray-50"
                      >
                        <div className="flex justify-between items-center mb-4">
                          <h4 className="font-medium text-gray-900">
                            Level {index + 1}
                          </h4>
                          <button
                            type="button"
                            onClick={() => removePackagingLevel(index)}
                            className="text-red-600 hover:text-red-800"
                          >
                            Remove
                          </button>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <input
                            type="text"
                            placeholder="Level (e.g., Unit, Box, Carton, Pallet)"
                            value={level.level}
                            onChange={(e) =>
                              updatePackagingLevel(
                                index,
                                "level",
                                e.target.value
                              )
                            }
                            className="border border-gray-300 rounded px-3 py-2"
                          />
                          <input
                            type="text"
                            placeholder="Description"
                            value={level.description}
                            onChange={(e) =>
                              updatePackagingLevel(
                                index,
                                "description",
                                e.target.value
                              )
                            }
                            className="border border-gray-300 rounded px-3 py-2"
                          />
                          <input
                            type="number"
                            placeholder="Quantity per level"
                            value={level.quantity}
                            onChange={(e) =>
                              updatePackagingLevel(
                                index,
                                "quantity",
                                e.target.value
                              )
                            }
                            className="border border-gray-300 rounded px-3 py-2"
                          />
                          <input
                            type="number"
                            step="0.01"
                            placeholder="Weight (kg)"
                            value={level.weight}
                            onChange={(e) =>
                              updatePackagingLevel(
                                index,
                                "weight",
                                e.target.value
                              )
                            }
                            className="border border-gray-300 rounded px-3 py-2"
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Industrial Features Tab */}
            {activeTab === "industrial" && (
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      HSN Code
                    </label>
                    <input
                      type="text"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      value={form.hsCode}
                      onChange={(e) => handleChange("hsCode", e.target.value)}
                      placeholder="e.g., 7304.19.00"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Country of Origin
                    </label>
                    <input
                      type="text"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      value={form.countryOfOrigin}
                      onChange={(e) =>
                        handleChange("countryOfOrigin", e.target.value)
                      }
                      placeholder="e.g., India"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="isHazmat"
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      checked={form.isHazmat}
                      onChange={(e) =>
                        handleChange("isHazmat", e.target.checked)
                      }
                    />
                    <label
                      htmlFor="isHazmat"
                      className="ml-2 text-sm font-medium text-gray-700"
                    >
                      Hazardous Material
                    </label>
                  </div>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="batchControlled"
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      checked={form.batchControlled}
                      onChange={(e) =>
                        handleChange("batchControlled", e.target.checked)
                      }
                    />
                    <label
                      htmlFor="batchControlled"
                      className="ml-2 text-sm font-medium text-gray-700"
                    >
                      Batch Controlled
                    </label>
                  </div>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="discountEligibility"
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      checked={form.discountEligibility}
                      onChange={(e) =>
                        handleChange("discountEligibility", e.target.checked)
                      }
                    />
                    <label
                      htmlFor="discountEligibility"
                      className="ml-2 text-sm font-medium text-gray-700"
                    >
                      Discount Eligible
                    </label>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Shelf Life (months)
                    </label>
                    <input
                      type="number"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      value={form.shelfLifeMonths}
                      onChange={(e) =>
                        handleChange("shelfLifeMonths", e.target.value)
                      }
                      placeholder="12"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Lead Time (days)
                    </label>
                    <input
                      type="number"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      value={form.leadTime}
                      onChange={(e) => handleChange("leadTime", e.target.value)}
                      placeholder="7"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Storage Conditions
                  </label>
                  <textarea
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    value={form.storageConditions}
                    onChange={(e) =>
                      handleChange("storageConditions", e.target.value)
                    }
                    placeholder="e.g., Store in cool, dry place. Temperature: 15-25°C"
                    rows={3}
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Special Instructions
                  </label>
                  <textarea
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    value={form.specialInstructions}
                    onChange={(e) =>
                      handleChange("specialInstructions", e.target.value)
                    }
                    placeholder="Special handling, safety instructions, etc."
                    rows={3}
                  />
                </div>
              </div>
            )}

            {/* Advanced Tab */}
            {activeTab === "advanced" && (
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Min Order Quantity
                    </label>
                    <input
                      type="number"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      value={form.minimumOrderQuantity}
                      onChange={(e) =>
                        handleChange("minimumOrderQuantity", e.target.value)
                      }
                      placeholder="1"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Max Order Quantity
                    </label>
                    <input
                      type="number"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      value={form.maximumOrderQuantity}
                      onChange={(e) =>
                        handleChange("maximumOrderQuantity", e.target.value)
                      }
                      placeholder="1000"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Reorder Point
                    </label>
                    <input
                      type="number"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      value={form.reorderPoint}
                      onChange={(e) =>
                        handleChange("reorderPoint", e.target.value)
                      }
                      placeholder="10"
                    />
                  </div>
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="isActive"
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    checked={form.isActive}
                    onChange={(e) => handleChange("isActive", e.target.checked)}
                  />
                  <label
                    htmlFor="isActive"
                    className="ml-2 text-sm font-medium text-gray-700"
                  >
                    Product is Active
                  </label>
                </div>
              </div>
            )}

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-center">
                  <svg
                    className="w-5 h-5 text-red-400 mr-2"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span className="text-red-700 font-medium">{error}</span>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex justify-end gap-3 pt-6 border-t border-gray-200">
              <button
                type="button"
                className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                onClick={() => navigate("/admin/products")}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="px-6 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all shadow-lg hover:shadow-xl disabled:opacity-50"
              >
                {submitting ? "Saving..." : "Update Product"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EditProduct;
